import { useEffect, useState } from "react";
import { getPublicCountyStats } from "../api";

interface CountyStatsResponse {
  // Only the fields this hook actually reads; the endpoint returns more
  // (see useTrackedPropertiesCount.ts for the counties[] shape).
  leads_total?: number;
}

/**
 * Fetch the live statewide leads-inventory total from the public
 * county-stats endpoint's top-level `leads_total` field (#1285 / #1562).
 *
 * Semantics (redefined by #1784 — see that issue for the "four disagreeing
 * definitions of lead" it reconciles): `leads_total` is the SAME population
 * the Leads tab (/leads, `leads_repository.go`) serves — every property in
 * status.BrowsableStatuses (notice_of_default / notice_filed /
 * auction_scheduled / pending_confirmation / postponed / code_violation,
 * i.e. Auction ∪ Lead ∪ Distress) with a usable mailing address — PLUS
 * notice_leads not yet promoted to a property row. Backed by migration 170's
 * `lead_inventory` / `dr_lead_inventory_summary` views. Before #1784 this
 * excluded live auctions (a different, scorecard-internal population that
 * had leaked into this number), which is why the landing hero and the Leads
 * tab used to show two different counts for the same claim. This also
 * replaces the drifted hardcoded `ADDRESSED_LEADS = LEAD_CATEGORIES.reduce(...)`
 * sum used in the landing hero (#1560), which understated live coverage by
 * ~50%.
 *
 * Graceful degradation: the handler OMITS `leads_total` entirely (rather
 * than sending 0) when the underlying view read fails, so the caller can
 * cleanly distinguish "no data available" from "genuinely zero leads."
 * A missing key surfaces here as `count === null`, matching the shape
 * `useTrackedPropertiesCount` returns on any failure.
 */
export function useAddressedLeadsCount() {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Public, unauthenticated endpoint — routed through the shared
    // apiClient (same reasoning as useTrackedPropertiesCount.ts:
    // X-Device-Id + 429 toast plumbing, safe when logged out because
    // apiClient only attaches Authorization when a session exists).
    getPublicCountyStats<CountyStatsResponse>()
      .then((payload) => {
        if (typeof payload?.leads_total === "number") {
          setCount(payload.leads_total);
        } else {
          // View-read failure path: handler omits the key entirely.
          // Leave count=null so the UI's ?? fallback kicks in.
          setCount(null);
        }
        setLoading(false);
      })
      .catch((e) => {
        setError(String(e));
        setLoading(false);
      });
  }, []);

  return { count, loading, error };
}
